import React, { useState } from 'react';
import { Dialog, VisuallyHidden } from '@radix-ui/themes';
import { Button, Badge } from '../common';
import { Heart, Clock, Xmark } from 'iconoir-react';
import ReportMenuButton from '../common/ReportMenuButton';
import type { AnonymousProfile } from '../../services/discoverService';
import { cleanProfileSummary } from '../../utils';

interface ProfileDetailModalProps {
  isOpen: boolean;
  onClose: () => void;
  profile: AnonymousProfile | null;
  onExpressInterest: (profileId: string) => void;
  isExpressingInterest?: boolean;
}

const URGENCY_CONFIG: Record<string, { label: string; color: 'error' | 'warning' | 'info' | 'gray' }> = {
  urgent: { label: 'Urgent', color: 'error' },
  time_sensitive: { label: 'Time Sensitive', color: 'warning' },
  ongoing: { label: 'Ongoing', color: 'info' },
  exploratory: { label: 'Exploring', color: 'gray' },
};

const ProfileDetailModal: React.FC<ProfileDetailModalProps> = ({
  isOpen,
  onClose,
  profile,
  onExpressInterest,
  isExpressingInterest = false,
}) => {
  const [isSummaryExpanded, setIsSummaryExpanded] = useState(false);

  if (!profile) return null;

  // Jun-16 (a user V1 test, O3): 'ongoing' is a universal hardcoded default
  // (the AI service writes it on every summary; onboarding never derives a real
  // urgency), so it carries no signal. Treat 'ongoing'/null as "no urgency set"
  // and show a badge only for a genuinely-chosen urgent / time_sensitive /
  // exploratory value.
  const urgencyConfig =
    profile.urgency && profile.urgency !== 'ongoing'
      ? URGENCY_CONFIG[profile.urgency]
      : undefined;

  const getTimeAgo = (dateString: string) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffDays = Math.floor((now.getTime() - date.getTime()) / (1000 * 60 * 60 * 24));

    if (diffDays === 0) return 'Today';
    if (diffDays === 1) return 'Yesterday';
    if (diffDays < 7) return `${diffDays} days ago`;
    if (diffDays < 30) return `${Math.floor(diffDays / 7)} weeks ago`;
    return `${Math.floor(diffDays / 30)} months ago`;
  };

  const handleExpressInterest = () => {
    onExpressInterest(profile.id);
  };

  return (
    <Dialog.Root open={isOpen} onOpenChange={(open) => !open && onClose()}>
      <Dialog.Content maxWidth="32rem" aria-describedby={undefined}>
        {/* Accessibility: Visually hidden title for screen readers */}
        <VisuallyHidden>
          <Dialog.Title>Profile Details: {profile.display_name}</Dialog.Title>
        </VisuallyHidden>
        <Dialog.Description>
          <VisuallyHidden>View detailed profile information and express interest</VisuallyHidden>
        </Dialog.Description>

        {/* Header */}
        <div className="flex items-start justify-between mb-4">
          <div className="flex-1">
            <h2 className="text-xl font-semibold text-heading-500 mb-1">
              {profile.display_name}
            </h2>
            <div className="flex items-center gap-2 text-sm text-body-400">
              <Clock className="w-4 h-4" />
              <span>Active {getTimeAgo(profile.last_active_at)}</span>
            </div>
          </div>
          <div className="flex items-center gap-1">
            <ReportMenuButton userId={profile.id} name={profile.display_name} />
            <button
              onClick={onClose}
              className="p-1 hover:bg-gray-100 rounded-full transition-colors"
              aria-label="Close modal"
            >
              <Xmark className="w-5 h-5 text-body-400" />
            </button>
          </div>
        </div>

        {/* Badges Row */}
        <div className="flex flex-wrap items-center gap-2 mb-4">
          {urgencyConfig && (
            <Badge size="sm" color={urgencyConfig.color}>
              {urgencyConfig.label}
            </Badge>
          )}
          {profile.compatibility_hint && (
            <span className="px-2 py-1 bg-primary-50 text-primary-600 text-sm font-semibold rounded-full">
              {profile.compatibility_hint}% Match
            </span>
          )}
        </div>

        {/* Profile Summary */}
        <div className="mb-4">
          <h3 className="text-sm font-medium text-heading-500 mb-2">About</h3>
          <p className={`text-sm text-body-600 ${isSummaryExpanded ? '' : 'line-clamp-4'}`}>
            {cleanProfileSummary(profile.profile_summary)}
          </p>
          {profile.profile_summary && profile.profile_summary.length > 200 && (
            <button
              onClick={() => setIsSummaryExpanded(!isSummaryExpanded)}
              className="text-sm text-primary-500 hover:text-primary-600 mt-1"
            >
              {isSummaryExpanded ? 'Show less' : 'Read more'}
            </button>
          )}
        </div>

        {/* Objectives */}
        {profile.objectives?.length > 0 && (
          <div className="mb-4">
            <h3 className="text-sm font-medium text-heading-500 mb-2">Objectives</h3>
            <div className="flex flex-wrap gap-2">
              {profile.objectives.map((objective, idx) => (
                <span
                  key={idx}
                  className="px-3 py-1.5 bg-gray-100 text-gray-700 text-sm rounded-full"
                >
                  {objective}
                </span>
              ))}
            </div>
          </div>
        )}

        {/* Member Info */}
        <div className="text-xs text-body-400 mb-6 pb-4 border-b border-gray-100">
          Member since {new Date(profile.member_since).toLocaleDateString('en-US', {
            month: 'long',
            year: 'numeric',
          })}
        </div>

        {/* Actions */}
        <div className="flex gap-3">
          <Button
            variant="outline"
            className="flex-1"
            onClick={onClose}
            disabled={isExpressingInterest}
          >
            Close
          </Button>
          <Button
            className="flex-1"
            onClick={handleExpressInterest}
            isLoading={isExpressingInterest}
          >
            <Heart className="w-4 h-4" />
            Express Interest
          </Button>
        </div>
      </Dialog.Content>
    </Dialog.Root>
  );
};

export default ProfileDetailModal;
